import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelection } from '../../../../../../store/reducers/selection';
import { useFormContext } from 'react-hook-form';
import { CONSENT_TO_PROCESSING_PERSONAL_DATA_URL, FORM_FIELDS, PAGE } from '../../../../../../constants';
import { getSignInParams, validatePhone } from '../helpers';
import { Button, Typography } from '@sravni/react-design-system';
import styles from './styles.module.scss';
import { SmsCodeField } from '../fields';
import { StepContext } from '../../../../creator/step.context';
import { getParamsForAuth } from '../../../../../form.common/helpers';
import { postMessage, PostMessageType } from '../../../../../../helpers/whitelable';
import { useFormData } from '../../../../../../store/selectors/formStore';
import { SETTINGS } from '../../../../steps/params/auth';
import { AuthSmsContext } from '../../../../creator/auth.context';
import { FocusedFieldProps } from '../../../types';
import { getCurrentField } from '../../helpers';

const { Heading, Text } = Typography;

export const PhoneFieldHeader = () => {
    return (
        <div className={styles['custom-sheet-header']}>
            <Heading level={2}>Вход или регистрация</Heading>
            <Text size={14} className={styles['custom-sheet-header__subtitle']}>
                Введите ваш номер телефона. На него мы отправим СМС с кодом подтверждения
            </Text>
        </div>
    );
};

export const SmSFieldHeader = () => {
    return (
        <div className={styles['custom-sheet-header']}>
            <Heading level={2}>Введите код из смс</Heading>
        </div>
    );
};

export const SmsCodeFieldFooter: FC = () => {
    return <></>;
};

export const PhoneFieldFooter: FC = () => {
    const { page } = useSelection();
    const form = useFormContext();
    const { setFocusedField } = useContext(StepContext);
    const { requestCode, inProcess, countdown } = useContext(AuthSmsContext);
    const [isBtnDisabled, setIsBtnDisabled] = useState(true);

    const {
        setValue,
        watch,
        formState: { dirtyFields: _ },
    } = form;

    const [phone] = watch([FORM_FIELDS.PHONE]);

    const onBtnClick = useCallback(async () => {
        const params = getSignInParams(phone, page);
        await requestCode(params);
        setValue(FORM_FIELDS.SMS_CODE, '');
        setFocusedField(FORM_FIELDS.SMS_CODE);
    }, [requestCode, phone, page, setFocusedField]);

    const checkPhoneIsValid = async () => {
        const correct = phone ? await validatePhone(phone) : false;
        setIsBtnDisabled(countdown > 0 || !correct);
    };

    useEffect(() => {
        checkPhoneIsValid();
    }, [phone, countdown]);

    return (
        <div className={styles['phone-field-footer']}>
            <Button
                variant="primary"
                disabled={isBtnDisabled}
                loading={inProcess}
                size={52}
                onClick={onBtnClick}
                className={styles['phone-field-footer__button']}>
                Получить код подтверждения {countdown > 0 ? ` (${countdown})` : ''}
            </Button>
            <Text size={12}>
                При входе, вы соглашаетесь с{' '}
                <a href={CONSENT_TO_PROCESSING_PERSONAL_DATA_URL} target="_blank" rel="noopener noreferrer">
                    правилами&nbsp;предоставления&nbsp;информации
                </a>
            </Text>
        </div>
    );
};

export const SmsCodeFieldForSheet: FC<FocusedFieldProps> = (props) => {
    const { fieldsList } = props;
    const form = useFormContext();
    const { setFocusedField } = useContext(StepContext);
    const formData = useFormData();
    const { page } = useSelection();
    const { checkCode, countdown, attempts, requestCode, inProcess, isSilentAuth } = useContext(AuthSmsContext);

    const {
        setValue,
        watch,
        formState: { dirtyFields: _ },
    } = form;

    const [phone, code] = watch([FORM_FIELDS.PHONE, FORM_FIELDS.SMS_CODE]);

    const onEdit = useCallback(() => {
        setFocusedField(FORM_FIELDS.PHONE);
    }, [setFocusedField]);

    const onCheckCode = async (code: string) => {
        const freshUserId = await checkCode({
            ...getParamsForAuth(formData),
            code,
        });
        if (!freshUserId) {
            return;
        }

        if (page === PAGE.AVITO) {
            postMessage({
                type: PostMessageType.event,
                label: 'broker_form_num_submit',
            });
        }
        if (isSilentAuth && fieldsList) {
            const { index } = getCurrentField(fieldsList, FORM_FIELDS.SMS_CODE);
            const nextField = fieldsList[index + 1];
            if (nextField) {
                setFocusedField(nextField.name);
            }
        }
    };

    const sendSms = useCallback(async () => {
        const params = getSignInParams(phone, page);
        await requestCode(params);
        setValue(FORM_FIELDS.SMS_CODE, '');
    }, [setValue, requestCode, phone, page]);

    useEffect(() => {
        if (code && code.length === 4) {
            onCheckCode(code);
        }
    }, [code]);

    return (
        <div className={styles['sms-field']}>
            <Text className={styles['sms-field__text']} size={14}>
                Мы отправили СМС с кодом подтверждения на номер{' '}
                <Button variant="text" color="blue" className={styles['sms-field__edit-btn']} onClick={onEdit}>
                    {phone} (изменить)
                </Button>
            </Text>
            <SmsCodeField />
            {attempts < SETTINGS.ATTEMPTS && (
                <div>
                    {countdown > 0 && (
                        <Text className={styles['sms-field__text']} size={14}>
                            Повторно код можно отправить через 0:{`${countdown < 10 ? 0 : ''}${countdown}`}
                        </Text>
                    )}
                    {countdown === 0 && (
                        <Button
                            variant="text"
                            color="blue"
                            className={styles['sms-field__edit-btn']}
                            onClick={sendSms}
                            disabled={inProcess}>
                            Запросить новый код
                        </Button>
                    )}
                </div>
            )}
        </div>
    );
};
